import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface StripeCardEntryProps {
  amount: number;
  businessId: string;
  bookingId?: string;
  proposalId?: string;
  payment_type?: 'deposit' | 'final' | 'partial';
  onSuccess: (paymentId: string) => void;
  onError: (error: string) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  surchargeEnabled?: boolean;
  surchargePercentage?: number;
  /** When set, shows “Save card on file” (controlled via saveCardOnFile + onSaveCardOnFileChange). */
  customerId?: string;
  saveCardOnFile?: boolean;
  onSaveCardOnFileChange?: (value: boolean) => void;
}

// Inner component that uses Stripe hooks
function StripeCardForm({ 
  amount, 
  businessId, 
  bookingId,
  proposalId,
  payment_type = 'final',
  onSuccess, 
  onError,
  onProcessingChange,
  surchargeEnabled = false,
  surchargePercentage = 0,
  customerId,
  saveCardOnFile = false,
  onSaveCardOnFileChange,
}: StripeCardEntryProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardFunding, setCardFunding] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [detectingCardType, setDetectingCardType] = useState(false);

  const isCreditCard = cardFunding === 'credit';
  const applySurcharge = surchargeEnabled && surchargePercentage > 0 && isCreditCard;
  const surchargeAmount = applySurcharge ? Math.round(amount * (surchargePercentage / 100) * 100) / 100 : 0;
  const totalAmount = Math.round((amount + surchargeAmount) * 100) / 100;

  useEffect(() => {
    if (onProcessingChange) {
      onProcessingChange(isProcessing);
    }
  }, [isProcessing, onProcessingChange]);

  // Detect card funding type (credit/debit) via createPaymentMethod when card is fully entered
  useEffect(() => {
    if (!cardComplete || !surchargeEnabled || surchargePercentage <= 0 || !stripe || !elements) {
      if (!cardComplete) {
        setCardFunding(null);
      }
      return;
    }

    const detectCardType = async () => {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      setDetectingCardType(true);
      try {
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (pmError) {
          console.error('Card type detection error:', pmError);
          setCardFunding(null);
        } else if (paymentMethod) {
          console.log('💳 Card type detected:', {
            brand: paymentMethod.card?.brand,
            funding: paymentMethod.card?.funding,
          });
          setCardFunding(paymentMethod.card?.funding || null);
        }
      } catch (err) {
        console.error('Card type detection failed:', err);
        setCardFunding(null);
      } finally {
        setDetectingCardType(false);
      }
    };

    detectCardType();
  }, [cardComplete, surchargeEnabled, surchargePercentage, stripe, elements]);

  const handleCardChange = (event: any) => {
    const isComplete = event.complete ?? false;
    if (!isComplete) {
      setCardFunding(null);
    }
    setCardComplete(isComplete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!stripe || !elements) {
      setError('Stripe not loaded. Please refresh and try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    // If surcharge is enabled but we haven't detected card type yet, detect now
    let finalFunding = cardFunding;
    let finalSurcharge = surchargeAmount;
    let finalTotal = totalAmount;

    if (surchargeEnabled && surchargePercentage > 0 && !cardFunding) {
      try {
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        if (paymentMethod?.card?.funding) {
          finalFunding = paymentMethod.card.funding;
          if (finalFunding === 'credit') {
            finalSurcharge = Math.round(amount * (surchargePercentage / 100) * 100) / 100;
            finalTotal = Math.round((amount + finalSurcharge) * 100) / 100;
          }
        }
      } catch (err) {
        console.error('Card type detection at submit failed:', err);
      }
    }

    console.log('💳 Starting payment process:', {
      amount,
      surchargeAmount: finalSurcharge,
      totalAmount: finalTotal,
      cardFunding: finalFunding,
      businessId,
      bookingId,
    });

    try {
      // Refresh session before edge function call — prevents stale JWT on iOS
      await supabase.auth.getSession();

      console.log('🔗 Creating Payment Intent...');
      const { data: intentData, error: intentError } = await supabase.functions.invoke(
        'create-payment-intent',
        {
          body: {
            businessId,
            bookingId: bookingId || undefined,
            amount: finalTotal,
            baseAmount: amount,
            surchargeAmount: finalSurcharge,
            description: bookingId ? `Payment for booking ${bookingId}` : 'Direct payment',
            metadata: {
              source: 'in_person_card_entry',
              payment_type: 'direct_card',
              base_amount: String(amount),
              surcharge_amount: String(finalSurcharge),
              card_funding: finalFunding || 'unknown',
            },
            ...(saveCardOnFile && customerId ? { saveCardForCustomerId: customerId } : {}),
          }
        }
      );

      console.log('📥 Payment Intent response:', { intentData, intentError });

      if (intentError) {
        console.error('❌ Payment Intent creation error:', intentError);
        throw new Error(intentError.message || 'Failed to create payment intent. Please ensure Stripe Connect is properly configured.');
      }

      const responseData = intentData?.data || intentData;
      
      if (!responseData?.clientSecret) {
        console.error('❌ No client secret in response. Full response:', { intentData, responseData });
        throw new Error('No client secret returned. Please ensure Stripe Connect is properly configured.');
      }

      console.log('✅ Payment Intent created, client secret received');

      console.log('💳 Confirming payment with Stripe...');
      const clientSecret = responseData.clientSecret;
      
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {}
          }
        }
      );

      console.log('📥 Payment confirmation response:', { confirmError, paymentIntent });

      if (confirmError) {
        console.error('❌ Payment confirmation error:', confirmError);
        throw new Error(confirmError.message || 'Payment failed. Please check your card details and try again.');
      }

      if (paymentIntent?.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        const { data: payment, error: paymentError } = await supabase
          .from('payments')
          .insert({
            business_id: businessId,
            booking_id: bookingId || null,
            proposal_id: proposalId || null,
            amount: finalTotal,
            base_amount: amount,
            surcharge_amount: finalSurcharge,
            method: 'credit_card',
            channel: 'card_online',
            status: 'completed',
            stripe_payment_intent_id: paymentIntent.id,
            reference: paymentIntent.id,
            notes: finalSurcharge > 0
              ? `Card payment via Stripe (includes $${finalSurcharge.toFixed(2)} processing fee)`
              : 'Direct card payment via Stripe',
            payment_type: payment_type
          })
          .select()
          .single();

        if (paymentError) {
          console.error('Failed to create payment record:', paymentError);
          onSuccess(paymentIntent.id);
        } else {
          if (saveCardOnFile && customerId && paymentIntent.id) {
            try {
              await supabase.auth.getSession();
              const { data: finRaw, error: finErr } = await supabase.functions.invoke(
                'finalize-customer-card-from-payment-intent',
                { body: { businessId, customerId, paymentIntentId: paymentIntent.id } },
              );
              const finPayload =
                finRaw && typeof finRaw === 'object' && 'data' in finRaw && finRaw.data && typeof finRaw.data === 'object'
                  ? (finRaw.data as Record<string, unknown>)
                  : (finRaw as Record<string, unknown> | null);
              if (finErr) {
                console.error('Save card on file failed:', finErr.message);
              } else if (finPayload?.error && typeof finPayload.error === 'string') {
                console.error('Save card on file failed:', finPayload.error);
              }
            } catch (saveErr) {
              console.error('Save card on file failed:', saveErr);
            }
          }
          onSuccess(payment.id);
        }
      } else {
        throw new Error('Payment was not completed. Please try again.');
      }
    } catch (err: any) {
      console.error('❌ Payment error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': {
          color: '#9CA3AF',
        },
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444',
      },
    },
    hidePostalCode: true, // Hide postal code field - not required for in-person payments
    disableLink: true, // Disable the autofill link button
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="px-4 py-3 bg-white border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {error && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {customerId && onSaveCardOnFileChange && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800">Save card on file</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">
              For faster checkout later. Replaces any existing saved card.
            </p>
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center">
            <input
              type="checkbox"
              checked={saveCardOnFile}
              onChange={(e) => onSaveCardOnFileChange(e.target.checked)}
              disabled={isProcessing}
              className="peer sr-only"
            />
            <div className="relative h-5 w-9 rounded-full bg-gray-200 transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500/30 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
          </label>
        </div>
      )}

      {cardComplete && surchargeEnabled && surchargePercentage > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1.5 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span>${amount.toFixed(2)}</span>
          </div>
          {detectingCardType ? (
            <div className="flex items-center text-gray-400 py-0.5">
              <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin mr-2"></div>
              <span>Detecting card type...</span>
            </div>
          ) : applySurcharge ? (
            <div className="flex justify-between text-amber-700">
              <span>Card Processing Fee ({surchargePercentage}%)</span>
              <span>${surchargeAmount.toFixed(2)}</span>
            </div>
          ) : cardFunding === 'debit' ? (
            <div className="flex justify-between text-green-600">
              <span>Debit card — no processing fee</span>
              <span>$0.00</span>
            </div>
          ) : null}
          <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-1.5">
            <span>Total</span>
            <span>${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e as any);
        }}
        disabled={isProcessing || !stripe}
        className="w-full px-4 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {isProcessing ? (
          <div className="flex items-center">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
            Processing Payment...
          </div>
        ) : (
          `Charge $${totalAmount.toFixed(2)}`
        )}
      </button>
    </div>
  );
}

// Main component that wraps with Elements provider
export default function StripeCardEntry(props: StripeCardEntryProps) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        // Refresh session before edge function calls — prevents stale JWT on iOS
        await supabase.auth.getSession();

        console.log('🔑 Fetching Stripe Connect configuration for business:', props.businessId);
        const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        const { data: keyData, error: keyError } = await supabase.functions.invoke(
          'get-stripe-connect-publishable-key',
          {
            body: { 
              businessId: props.businessId,
              platformPublishableKey: platformKey
            }
          }
        );

        const resolvedData = keyData?.data || keyData;

        if (keyError || !resolvedData?.publishableKey || !resolvedData?.accountId) {
          console.error('❌ Failed to get Stripe Connect configuration:', keyError, keyData);
          setError('Failed to get Stripe Connect configuration. Please ensure Stripe Connect is properly configured.');
          setLoading(false);
          return;
        }

        console.log('✅ Got Stripe Connect configuration:', {
          accountId: resolvedData.accountId,
          publishableKeyPrefix: resolvedData.publishableKey.substring(0, 20) + '...'
        });
        
        const stripe = await loadStripe(resolvedData.publishableKey, {
          stripeAccount: resolvedData.accountId
        } as any);
        setStripePromise(Promise.resolve(stripe));
        setLoading(false);
      } catch (err: any) {
        console.error('Stripe initialization error:', err);
        setError(err.message || 'Failed to initialize payment system');
        setLoading(false);
      }
    };

    initializeStripe();
  }, [props.businessId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading payment form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-start text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="text-sm font-medium block mb-1">{error}</span>
            <span className="text-xs text-red-600">
              Please ensure Stripe Connect is properly configured in your business settings.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return null;
  }

  const elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe' as const,
    },
    // Disable autofill link
    loader: 'auto',
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripeCardForm {...props} />
    </Elements>
  );
}

